import { capitalCase } from "change-case";
import { useRef, useState } from "react";

import {
  APPT_DEFAULT_DURATION,
  APPT_DEFAULT_DURATION_UNIT,
  FieldComponentType,
  sectionFallback,
} from "@smart/bridge-intake-components-dom";
import { FieldNS, FieldType, GroupNS } from "@smart/bridge-types-basic";
import {
  DeepPartial,
  getLocalTimeZoneCode,
  isNotNullOrUndefined,
  rankBetween,
  removeEmptyValues,
  removeKeys,
  removeKeysRecursively,
} from "@smart/itops-utils-basic";
import {
  useDeleteField,
  useDeleteGroup,
  useDeleteSection,
  useUpdateAIUserFeedback,
  useUpdateField,
  useUpdateFieldOrder,
  useUpdateFormPublishStatus,
  useUpdateGroup,
  useUpdateGroupOrder,
  useUpdateGroupWithFields,
  useUpdateSection,
} from "@smart/manage-gql-client-dom";
import {
  useAnalyticsEvent,
  useDuplicateGroupFull,
  useDuplicateSectionFull,
} from "@smart/manage-hooks-dom";

import { baseLanguageKey } from "../../../types";
import { fieldComponentNames } from "../constants";
import { getDefaultSignatureMethod } from "../questions/signature/utils";
import {
  CreateMappedFields,
  EditableSection,
  UpdatedSection,
  GqlField,
  GqlForm,
  GqlGroup,
  GqlFieldValues,
  GqlGroupValues,
  GroupOptions,
  PendingField,
} from "../types";
import { getDefaultAvailability } from "../utils";

const getInitialFieldValues = (
  fieldType: FieldType,
  options?: { signatureMethod?: "electronic" | "digital" },
): Partial<GqlFieldValues> | undefined => {
  switch (fieldType) {
    case "appointment":
      return {
        label: "Appointment",
        duration: APPT_DEFAULT_DURATION,
        durationUnit: APPT_DEFAULT_DURATION_UNIT,
        availability:
          getDefaultAvailability() as GqlFieldValues["availability"],
        timezone: getLocalTimeZoneCode(),
        meetingType: "inPerson",
      };
    case "signature":
      return {
        label: "Signature",
        signature: {
          method: options?.signatureMethod ?? "electronic",
        } as GqlFieldValues["signature"],
      };
    default:
      return undefined;
  }
};

export const useUpdateData = ({
  formUri,
  form,
  sectionFields,
  sectionGroups,
  createForm,
  currentSectionUri,
  setCurrentSectionUri,
  shouldCreateInitialSection,
  viewingLanguage,
  digitalSignatureSupported,
  standardSignatureDisabled,
  checkDigitalSignatureAvailability,
  hasSubmissionLinks,
}: {
  formUri: string;
  form?: GqlForm | null;
  sectionFields: GqlField[] | undefined;
  sectionGroups: GqlGroup[] | undefined;
  createForm: () => Promise<void>;
  currentSectionUri: string;
  setCurrentSectionUri: (uri: string) => void;
  shouldCreateInitialSection: boolean;
  viewingLanguage: string;
  digitalSignatureSupported?: boolean;
  standardSignatureDisabled?: boolean;
  checkDigitalSignatureAvailability?: () => Promise<boolean>;
  hasSubmissionLinks?: boolean;
}) => {
  const [updateSectionMutation] = useUpdateSection();
  const [deleteSectionMutation] = useDeleteSection();
  const [updateGroupMutation] = useUpdateGroup();
  const [updateGroupOrder] = useUpdateGroupOrder(false);
  const [updateGroupWithFieldsMutation] = useUpdateGroupWithFields();
  const [deleteGroup] = useDeleteGroup();
  const [updateFieldMutation] = useUpdateField();
  const [updateFieldOrder] = useUpdateFieldOrder(false);
  const [deleteField] = useDeleteField();
  const [updateAIUserFeedbackMutation] = useUpdateAIUserFeedback();
  const { publishEvent } = useAnalyticsEvent();
  const [duplicateSectionFull] = useDuplicateSectionFull({ formUri });
  const [duplicateGroupFull] = useDuplicateGroupFull({ formUri });
  const [updateFormPublishStatusMutation] = useUpdateFormPublishStatus();

  const publishDigitalSignatureEvent = () =>
    publishEvent({
      category: "track",
      name: "IntakeDigitalSignatureAdded",
      screen: "FormBuilder",
      entryPoint: "FormBuilderEditView",
      component: "Digital Signature Component",
      jiraKey: "LINK-3675",
      interactionType: "Active",
    });

  const forLanguage =
    viewingLanguage === baseLanguageKey ? undefined : viewingLanguage;

  const [newItemUri, setNewItemUri] = useState<string>();
  const [signatureFieldUri, setSignatureFieldUri] = useState<string>();
  const [pendingFields, setPendingFields] = useState<PendingField[]>([]);
  const pendingSignatureUriRef = useRef<string | undefined>(undefined);
  const [updatingItems, setUpdatingItems] = useState<
    Record<string, boolean | undefined>
  >({});
  const setIsUpdating = (uri: string, value: boolean) =>
    setUpdatingItems((prev) => ({ ...prev, [uri]: value }));

  const updateFormPublishStatus = async () => {
    await updateFormPublishStatusMutation({
      variables: {
        uri: formUri,
        hasUnpublishedChanges: true,
      },
    });
  };

  const updateAIUserFeedback: typeof updateAIUserFeedbackMutation = async (
    ...args
  ) => {
    const uri = args?.[0]?.variables?.uri;
    const status = args?.[0]?.variables?.status;
    if (uri) setIsUpdating(uri, true);
    const result = await updateAIUserFeedbackMutation(...args);
    if (status === "completed") {
      await updateFormPublishStatus();
    }
    if (uri) setIsUpdating(uri, false);
    return result;
  };

  const updateSection: typeof updateSectionMutation = async (...args) => {
    const uri = args?.[0]?.variables?.uri;
    if (uri) setIsUpdating(uri, true);
    const result = await updateSectionMutation(...args);
    await updateFormPublishStatus();
    if (uri) setIsUpdating(uri, false);
    return result;
  };

  const updateGroup: typeof updateGroupMutation = async (...args) => {
    const uri = args?.[0]?.variables?.uri;
    if (uri) setIsUpdating(uri, true);
    const result = await updateGroupMutation(...args);
    await updateFormPublishStatus();
    if (uri) setIsUpdating(uri, false);
    return result;
  };

  const updateField: typeof updateFieldMutation = async (...args) => {
    const uri = args?.[0]?.variables?.uri;
    if (uri) setIsUpdating(uri, true);
    const result = await updateFieldMutation(...args);
    await updateFormPublishStatus();
    if (uri) setIsUpdating(uri, false);
    return result;
  };

  const updateGroupWithFields: typeof updateGroupWithFieldsMutation = async (
    ...args
  ) => {
    const uri = args?.[0]?.variables?.uri;
    if (uri) setIsUpdating(uri, true);
    const result = await updateGroupWithFieldsMutation(...args);
    await updateFormPublishStatus();
    if (uri) setIsUpdating(uri, false);
    return result;
  };

  const createFormAndSection = async () => {
    if (!form) await createForm();
    if (shouldCreateInitialSection) {
      await updateSection({
        variables: {
          uri: currentSectionUri,
          formUri,
          order: rankBetween({}),
          fields: {
            title: sectionFallback,
          },
        },
      });
    }
  };

  const isFieldMapped = (
    fieldValues: Pick<DeepPartial<GqlFieldValues>, "layout" | "field">,
    shouldRemoveMappedLayout?: boolean,
  ) => !shouldRemoveMappedLayout && !!(fieldValues.layout && fieldValues.field);

  const getMappedFieldCountInGroup = (
    groupUri: string,
    options?: {
      excludeFieldUri?: string;
      updatedField?: {
        uri: string;
        current: GqlFieldValues;
        incoming: DeepPartial<GqlFieldValues> & { withGroupUpdate?: boolean };
        shouldRemoveMappedLayout?: boolean;
      };
    },
  ) =>
    (sectionFields ?? []).filter((f) => {
      if (f.uri === options?.excludeFieldUri) return false;

      if (options?.updatedField?.uri === f.uri) {
        const { current, incoming, shouldRemoveMappedLayout } =
          options.updatedField;
        const merged = { ...current, ...incoming };
        const effectiveGroupUri =
          incoming.groupUri !== undefined ? incoming.groupUri : merged.groupUri;
        if (effectiveGroupUri !== groupUri) return false;
        return isFieldMapped(merged, shouldRemoveMappedLayout);
      }

      if (f.values.groupUri !== groupUri) return false;
      return isFieldMapped(f.values);
    }).length;

  const resetLayoutContactGroupIfEmpty = async (
    groupUri: string | undefined | null,
    options?: {
      excludeFieldUri?: string;
      updatedField?: {
        uri: string;
        current: GqlFieldValues;
        incoming: DeepPartial<GqlFieldValues> & { withGroupUpdate?: boolean };
        shouldRemoveMappedLayout?: boolean;
      };
    },
  ) => {
    if (!groupUri) return;

    const group = sectionGroups?.find((g) => g.uri === groupUri);
    if (!group || group.values.type !== "layoutContact") return;
    if (getMappedFieldCountInGroup(groupUri, options) > 0) return;

    await updateGroup({
      variables: {
        uri: groupUri,
        formUri,
        sectionUri: currentSectionUri,
        order: group.values.order,
        forLanguage,
        fields: {
          label: group.values.label,
          hint: group.values.hint,
          type: "custom",
          layout: undefined,
          field: undefined,
        },
      },
    });
  };

  const mergeUpdateField = async ({
    current,
    incoming,
    shouldRemoveMappedLayout,
  }: {
    current: GqlFieldValues;
    incoming: DeepPartial<GqlFieldValues> & { withGroupUpdate?: boolean };
    shouldRemoveMappedLayout?: boolean;
  }) => {
    const mergedFields = {} as GqlFieldValues & { withGroupUpdate?: boolean };
    Object.assign(mergedFields, current, incoming);

    const affectsGroupMapping =
      !!(current.layout && current.field) ||
      shouldRemoveMappedLayout ||
      incoming.groupUri !== undefined ||
      incoming.layout !== undefined ||
      incoming.field !== undefined;

    const addingDigitalSignature =
      incoming.signature?.method === "digital" &&
      current.signature?.method !== "digital";

    const result = await updateField({
      variables: {
        uri: mergedFields.uri,
        formUri,
        sectionUri: mergedFields.sectionUri,
        groupUri: mergedFields.groupUri || undefined,
        order: mergedFields.order,
        withGroupUpdate: mergedFields.withGroupUpdate,
        forLanguage,
        fields: {
          label: mergedFields.label,
          description: mergedFields.description ?? undefined,
          hint: mergedFields.hint,
          type: mergedFields.type,
          mandatory: mergedFields.mandatory || false,
          options: mergedFields.options
            .filter(Boolean)
            .map((option) => removeKeys(option, ["__typename"])),
          supportsCustomValues: mergedFields.supportsCustomValues,
          allowCustomResponse: mergedFields.allowCustomResponse || undefined,
          layout:
            shouldRemoveMappedLayout || !mergedFields.layout
              ? undefined
              : (removeEmptyValues(
                  removeKeys(mergedFields.layout, [
                    "__typename",
                    "displayName",
                  ]),
                ) as GqlField["values"]["layout"]),
          field:
            shouldRemoveMappedLayout || !mergedFields.field
              ? undefined
              : {
                  ...removeKeys(mergedFields.field, [
                    "__typename",
                    "displayName",
                    "details",
                  ]),
                  possibleValues:
                    mergedFields.field.possibleValues || undefined,
                  dataSourceMapping:
                    mergedFields.field.dataSourceMapping || undefined,
                  originalName: mergedFields.field.originalName || undefined,
                },
          links: mergedFields.links
            ? mergedFields.links.map((f) => removeKeys(f, ["__typename"]))
            : undefined,
          allowCopyFromFieldUri:
            mergedFields.allowCopyFromFieldUri || undefined,
          availableStaffIds:
            mergedFields.availableStaffIds?.filter(Boolean) || undefined,
          duration: mergedFields.duration || undefined,
          durationUnit: mergedFields.durationUnit || undefined,
          availability: mergedFields.availability
            ? removeKeysRecursively(mergedFields.availability.filter(Boolean), [
                "__typename",
              ])
            : undefined,
          timezone: mergedFields.timezone || undefined,
          bufferTime: mergedFields.bufferTime || undefined,
          minimumNotice: mergedFields.minimumNotice || undefined,
          meetingType: mergedFields.meetingType || undefined,
          skipEventInvite: mergedFields.skipEventInvite || undefined,
          includeInSummary: mergedFields.includeInSummary || undefined,
          payment: mergedFields.payment
            ? {
                ...removeKeys(removeEmptyValues(mergedFields.payment), [
                  "__typename",
                ]),
                bankAccount: mergedFields.payment.bankAccount
                  ? removeKeys(mergedFields.payment.bankAccount, ["__typename"])
                  : undefined,
              }
            : undefined,
          signature: mergedFields.signature
            ? removeKeys(mergedFields.signature, ["__typename"])
            : undefined,
        },
      },
    });

    if (addingDigitalSignature) {
      publishDigitalSignatureEvent().catch(console.error);
    }

    if (affectsGroupMapping && current.groupUri) {
      await resetLayoutContactGroupIfEmpty(current.groupUri, {
        updatedField: {
          uri: mergedFields.uri,
          current,
          incoming,
          shouldRemoveMappedLayout,
        },
      });
    }

    return result;
  };

  const deleteFieldWithGroupReset: typeof deleteField = async (options) => {
    const fieldUri = options?.variables?.uri;
    const field = sectionFields?.find((f) => f.uri === fieldUri);
    const groupUri = field?.values.groupUri;
    const wasMapped = !!(field?.values.layout && field?.values.field);

    const result = await deleteField(options);

    if (wasMapped && groupUri) {
      await resetLayoutContactGroupIfEmpty(groupUri, {
        excludeFieldUri: fieldUri,
      });
    }

    await updateFormPublishStatus();

    return result;
  };

  const deleteGroupWithPublishStatus: typeof deleteGroup = async (...args) => {
    const result = await deleteGroup(...args);
    await updateFormPublishStatus();
    return result;
  };

  const mergeUpdateGroup = ({
    current,
    incoming,
  }: {
    current: GqlGroupValues;
    incoming: DeepPartial<GqlGroupValues>;
  }) => {
    const mergedGroup = {} as GqlGroupValues;
    Object.assign(mergedGroup, current, incoming);

    return updateGroup({
      variables: {
        uri: mergedGroup.uri,
        formUri: mergedGroup.formUri,
        sectionUri: mergedGroup.sectionUri,
        order: mergedGroup.order,
        forLanguage,
        fields: {
          label: mergedGroup.label || undefined,
          description: mergedGroup.description || undefined,
          hint: mergedGroup.hint,
          type: mergedGroup.type,
          field: mergedGroup.field
            ? {
                ...removeKeys(mergedGroup.field, [
                  "__typename",
                  "displayName",
                  "details",
                ]),
                possibleValues: mergedGroup.field.possibleValues || undefined,
                dataSourceMapping:
                  mergedGroup.field.dataSourceMapping || undefined,
                originalName: mergedGroup.field.originalName || undefined,
              }
            : undefined,
          layout: mergedGroup.layout
            ? {
                ...removeKeys(mergedGroup.layout, [
                  "__typename",
                  "displayName",
                ]),
                parentId: mergedGroup.layout.parentId || undefined,
                parentName: mergedGroup.layout.parentName || undefined,
                parentProviderId:
                  mergedGroup.layout.parentProviderId || undefined,
              }
            : undefined,
          links: mergedGroup.links
            ? mergedGroup.links.map((link) => removeKeys(link, ["__typename"]))
            : undefined,
          allowedRepeatable: mergedGroup.allowedRepeatable || undefined,
          maxRepeat: mergedGroup.maxRepeat || undefined,
          minRepeat: mergedGroup.minRepeat || undefined,
          repeatable: mergedGroup.repeatable || undefined,
          templateType: mergedGroup.templateType || undefined,
        },
      },
    });
  };

  const updateExistingGroupForLayoutContact = async (group: GroupOptions) => {
    const existingGroup = sectionGroups?.find((g) => g.uri === group.uri);
    if (
      existingGroup &&
      group.type === "layoutContact" &&
      existingGroup.values.type !== "layoutContact"
    ) {
      const mappedFieldsInGroup =
        sectionFields?.filter(
          (f) =>
            f.values.groupUri === existingGroup.uri &&
            f.values.layout &&
            f.values.field,
        ) || [];
      if (mappedFieldsInGroup.length > 0) {
        throw new Error(
          "Group has to be a LayoutContact group or has no mapped fields!",
        );
      }

      await updateGroup({
        variables: {
          uri: existingGroup.uri,
          formUri,
          sectionUri: currentSectionUri,
          forLanguage,
          fields: {
            label: existingGroup.values.label,
            hint: existingGroup.values.hint,
            type: group.type,
            layout: group.layout,
            field: group.field
              ? {
                  name: group.field.name,
                  type: group.field.type,
                  possibleValues: group.field.possibleValues || undefined,
                  dataSourceMapping: group.field.dataSourceMapping || undefined,
                  originalName: group.field.originalName || undefined,
                }
              : undefined,
          },
          order: existingGroup.values.order,
        },
      });
    }
  };

  const addMappedFields: CreateMappedFields = async ({
    newFields,
    removedFieldUris,
    beforePosition,
    afterPosition,
    destinationGroupUri,
    group,
  }) => {
    await createFormAndSection();

    if (group?.layout && group?.field) {
      let fieldOrderAfter: string | undefined;

      // Group doesn't exist, create group and fields
      if (!destinationGroupUri) {
        await updateGroupWithFields({
          variables: {
            uri: GroupNS.generateUri(),
            formUri,
            sectionUri: currentSectionUri,
            forLanguage,
            fields: {
              label: group.label,
              hint: "",
              type: group.type,
              layout: group.layout,
              field: group.field
                ? {
                    name: group.field.name,
                    type: group.field.type,
                    possibleValues: group.field.possibleValues || undefined,
                    dataSourceMapping:
                      group.field.dataSourceMapping || undefined,
                    originalName: group.field.originalName || undefined,
                  }
                : undefined,
              fields: newFields.map((newField) => {
                fieldOrderAfter = rankBetween({ after: fieldOrderAfter });

                return {
                  uri: newField.uri,
                  type: newField.type,
                  label: newField.label || "",
                  hint: "",
                  options: newField.options || [],
                  supportsCustomValues: newField.supportsCustomValues,
                  mandatory: false,
                  allowCustomResponse: newField.allowCustomResponse,
                  layout: newField.layout,
                  field: newField.field,
                  order: fieldOrderAfter,
                };
              }),
            },
            order: rankBetween({
              after: afterPosition,
              before: beforePosition,
            }),
          },
        });

        return;
      }

      await updateExistingGroupForLayoutContact(group);
    }

    let orderAfter = afterPosition;

    await Promise.all(
      newFields.map((newField) => {
        const order = rankBetween({
          after: orderAfter,
          before: beforePosition,
        });

        const promise = updateField({
          variables: {
            formUri,
            groupUri: destinationGroupUri || undefined,
            sectionUri: currentSectionUri,
            uri: newField.uri,
            order,
            forLanguage,
            fields: {
              type: newField.type,
              label: newField.label || "",
              hint: "",
              options: newField.options || [],
              supportsCustomValues: newField.supportsCustomValues,
              mandatory: false,
              allowCustomResponse: newField.allowCustomResponse,
              layout: newField.layout,
              field: newField.field,
            },
          },
        });
        orderAfter = order;
        publishEvent({
          category: "track",
          name: "FormBuilderComponentAdded",
          screen: "FormBuilder",
          entryPoint: "FormBuilderEditView",
          component: "FormBuilderComponents",
          jiraKey: "LINK-3003",
          interactionType: "Active",
          properties: {
            ComponentName: "MappedField",
          },
        }).catch(console.error);
        return promise;
      }),
    );

    if (!removedFieldUris?.length) return;

    await Promise.all(
      removedFieldUris.map((uri) => {
        const field = sectionFields?.find((f) => f.uri === uri);
        if (!field) return undefined;

        return deleteFieldWithGroupReset({
          variables: {
            uri,
            sectionUri: field.values.sectionUri,
            formUri: field.values.formUri,
          },
        });
      }),
    );
  };

  const addGroup = async (order: string) => {
    const uri = GroupNS.generateUri();
    try {
      await createFormAndSection();

      setIsUpdating(uri, true);
      await updateGroup({
        variables: {
          uri,
          formUri,
          sectionUri: currentSectionUri,
          order,
          forLanguage,
          fields: {
            label: fieldComponentNames.group,
            hint: "",
            type: "custom",
            allowedRepeatable: true,
          },
        },
      });
      setNewItemUri(uri);
      publishEvent({
        category: "track",
        name: "FormBuilderComponentAdded",
        screen: "FormBuilder",
        entryPoint: "FormBuilderEditView",
        component: "FormBuilderComponents",
        jiraKey: "LINK-3003",
        interactionType: "Active",
        properties: {
          ComponentName: "GroupOfFields",
        },
      }).catch(console.error);
    } catch (error) {
      console.error("Error adding new group: ", error);
    } finally {
      setIsUpdating(uri, false);
    }
  };

  const addContactDetailsFields: CreateMappedFields = async ({
    newFields,
    beforePosition,
    afterPosition,
    group,
  }) => {
    if (!newFields.length || !group) return;

    await createFormAndSection();

    let fieldOrderAfter: string | undefined;

    await updateGroupWithFields({
      variables: {
        uri: GroupNS.generateUri(),
        formUri,
        sectionUri: currentSectionUri,
        forLanguage,
        fields: {
          label: group.label,
          hint: "",
          type: group.type,
          layout: group.layout,
          field: group.field
            ? {
                name: group.field.name,
                type: group.field.type,
                possibleValues: group.field.possibleValues || undefined,
                dataSourceMapping: group.field.dataSourceMapping || undefined,
                originalName: group.field.originalName || undefined,
              }
            : undefined,
          fields: newFields.map((newField) => {
            fieldOrderAfter = rankBetween({ after: fieldOrderAfter });

            return {
              uri: newField.uri,
              type: newField.type,
              label: newField.label || "",
              hint: "",
              options: newField.options || [],
              supportsCustomValues: newField.supportsCustomValues,
              mandatory: false,
              allowCustomResponse: newField.allowCustomResponse,
              layout: newField.layout,
              field: newField.field,
              order: fieldOrderAfter,
            };
          }),
        },
        order: rankBetween({ after: afterPosition, before: beforePosition }),
      },
    });
  };

  const addField = async ({
    type,
    order,
    groupUri,
  }: {
    type: FieldComponentType;
    order: string;
    groupUri?: string;
  }) => {
    if (
      !sectionFields ||
      type === "mapped" ||
      type === "group" ||
      type === "contactDetails"
    )
      return;

    const uri = FieldNS.generateUri();
    const isDigitalOnlySignature =
      type === "signature" &&
      digitalSignatureSupported === true &&
      standardSignatureDisabled === true;

    if (isDigitalOnlySignature) {
      // One setup check at a time, otherwise a second drop duplicates the
      // check and its warning toast.
      if (pendingSignatureUriRef.current) return;
      pendingSignatureUriRef.current = uri;
      setPendingFields((current) => [
        ...current,
        {
          uri,
          type: "signature",
          label: fieldComponentNames.signature,
          order,
          sectionUri: currentSectionUri,
          groupUri: groupUri || null,
        },
      ]);
      setIsUpdating(uri, true);
    }

    try {
      if (
        isDigitalOnlySignature &&
        !(await checkDigitalSignatureAvailability?.())
      ) {
        return;
      }

      await createFormAndSection();
      setIsUpdating(uri, true);
      const storedSignatureDefault =
        type === "signature" &&
        digitalSignatureSupported &&
        !standardSignatureDisabled &&
        !hasSubmissionLinks
          ? getDefaultSignatureMethod()
          : undefined;
      const initial = getInitialFieldValues(type, {
        signatureMethod: isDigitalOnlySignature
          ? "digital"
          : (storedSignatureDefault ?? "electronic"),
      });

      await updateField({
        variables: {
          uri,
          formUri,
          groupUri: groupUri || undefined,
          sectionUri: currentSectionUri,
          order,
          forLanguage,
          fields: {
            label: initial?.label || fieldComponentNames[type],
            type,
            hint: "",
            options: [],
            mandatory: false,
            ...initial,
          },
        },
      });
      setNewItemUri(uri);
      if (
        type === "signature" &&
        !isDigitalOnlySignature &&
        !storedSignatureDefault
      ) {
        setSignatureFieldUri(uri);
      }
      publishEvent({
        category: "track",
        name: "FormBuilderComponentAdded",
        screen: "FormBuilder",
        entryPoint: "FormBuilderEditView",
        component: "FormBuilderComponents",
        jiraKey: "LINK-3003",
        interactionType: "Active",
        properties: {
          ComponentName: capitalCase(fieldComponentNames[type], {
            delimiter: "",
          }),
        },
      }).catch(console.error);
      if (isDigitalOnlySignature || storedSignatureDefault === "digital") {
        publishDigitalSignatureEvent().catch(console.error);
      }
    } catch (error) {
      console.error("Error adding new field: ", error);
    } finally {
      if (pendingSignatureUriRef.current === uri) {
        pendingSignatureUriRef.current = undefined;
      }
      setPendingFields((current) =>
        current.filter((field) => field.uri !== uri),
      );
      setIsUpdating(uri, false);
    }
  };

  const changeGroupOrder = async (
    groupUri: string | undefined,
    order: string,
  ) => {
    if (!groupUri) return;

    try {
      setIsUpdating(groupUri, true);
      await updateGroupOrder({
        variables: {
          uri: groupUri,
          formUri,
          sectionUri: currentSectionUri,
          order,
        },
      });
      await updateFormPublishStatus();
    } catch (error) {
      console.error("Error updating field order: ", error);
    } finally {
      setIsUpdating(groupUri, false);
    }
  };

  const changeFieldOrder = async (
    fieldUri: string | undefined,
    order: string,
  ) => {
    if (!fieldUri) return;

    try {
      setIsUpdating(fieldUri, true);
      await updateFieldOrder({
        variables: {
          uri: fieldUri,
          formUri,
          sectionUri: currentSectionUri,
          order,
        },
      });
      await updateFormPublishStatus();
    } catch (error) {
      console.error("Error updating field order: ", error);
    } finally {
      setIsUpdating(fieldUri, false);
    }
  };

  const updateFormSection = async (
    section: EditableSection,
    values: { title: string; description?: string | null },
  ) => {
    if (!form) await createForm();

    setIsUpdating(section.uri, true);
    await updateSection({
      variables: {
        uri: section.uri,
        formUri,
        order: section.values?.order || rankBetween({}),
        forLanguage,
        fields: {
          title: values.title,
          description: isNotNullOrUndefined(values.description)
            ? values.description
            : section.values?.description || undefined,
          links: section.values?.links
            ? section.values?.links.map((link) => ({
                ...removeKeys(link, ["__typename"]),
              }))
            : undefined,
        },
      },
    });
    setIsUpdating(section.uri, false);
  };

  const updateAllSections = async (sections: UpdatedSection[]) => {
    if (!form) await createForm();

    const hasDeletions = sections.some(
      (section) => section.deleted && !section.created,
    );

    await Promise.allSettled(
      sections.map((section) => {
        if (section.deleted) {
          if (section.created) return undefined;

          return deleteSectionMutation({
            variables: { formUri, uri: section.uri },
          });
        }

        if (section.copySectionUri) {
          setCurrentSectionUri(section.uri);
          return duplicateSectionFull({
            newFormUri: formUri,
            sectionUri: section.copySectionUri,
            newSectionUri: section.uri,
            newSectionTitle: section.title,
            newSectionOrder: section.order,
            remove: [
              "fieldsMapping",
              "groupsMapping",
              "sectionLinks",
              "fieldsLinks",
              "groupsLinks",
            ],
            transformContactDetails: true,
          });
        }

        if (section.updated) {
          if (section.created) setCurrentSectionUri(section.uri);

          return updateSection({
            variables: {
              formUri,
              uri: section.uri,
              forLanguage,
              fields: {
                title: section.title,
                description: section.description || undefined,
                links: section.links
                  ? section.links.map((link) =>
                      removeKeys(link, ["__typename"]),
                    )
                  : undefined,
              },
              order: section.order,
            },
          });
        }
        return Promise.resolve();
      }),
    );

    if (hasDeletions) {
      await updateFormPublishStatus();
    }
  };

  const updateFieldGroup = async (
    fieldUri: string | undefined,
    groupUri: string | undefined,
    order: string,
    shouldRemoveMappedLayout?: boolean,
  ) => {
    if (!fieldUri) return;

    const field = sectionFields?.find((f) => f.uri === fieldUri);
    if (!field) return;

    try {
      setIsUpdating(fieldUri, true);
      await mergeUpdateField({
        current: field.values,
        incoming: {
          groupUri,
          order,
          withGroupUpdate: true,
        },
        shouldRemoveMappedLayout,
      });
    } catch (error) {
      console.error("Error updating field group: ", error);
    } finally {
      setIsUpdating(fieldUri, false);
    }
  };

  const duplicateGroup = async ({
    groupToCopyUri,
    newGroupTitle,
    newGroupOrder,
  }: {
    groupToCopyUri: string;
    newGroupTitle?: string;
    newGroupOrder: string;
  }) => {
    await duplicateGroupFull({
      newGroupTitle,
      newFormUri: formUri,
      groupUri: groupToCopyUri,
      newGroupOrder,
      remove: ["fieldsLinks", "fieldsMapping", "groupsLinks", "groupsMapping"],
    });
  };

  return {
    addMappedFields,
    addGroup,
    addContactDetailsFields,
    updateGroup,
    updateExistingGroupForLayoutContact,
    changeGroupOrder,
    deleteGroup: deleteGroupWithPublishStatus,
    addField,
    updateField,
    changeFieldOrder,
    deleteField: deleteFieldWithGroupReset,
    updateSection,
    updateFormSection,
    updateAllSections,
    newItemUri,
    setNewItemUri,
    signatureFieldUri,
    setSignatureFieldUri,
    pendingFields,
    updatingItems,
    updateFieldGroup,
    duplicateGroup,
    updateAIUserFeedback,
    mergeUpdateField,
    mergeUpdateGroup,
  };
};
